import * as React from "react";
import { useState } from "react";
import { useTheme } from "@mui/material/styles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  OutlinedInput,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Button,
  TextField,
  Card,
  IconButton,
  CardHeader,
  CardContent,
  FormHelperText,
  Box,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { PhotoCamera, Delete } from "@mui/icons-material";
import {
  Prefixes,
  Genders,
  BloodGroups,
  NationalIdTypes,
  BloodGroupOptions,
} from "../Utility/Constants";
import { addPatient } from "../API/PatientsApi";
import { useSnackbar } from "../Utility/UserMessageContext";
import { useContext } from "react";
import authContext from "../Context/AuthProvider";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import {
  calculateAge,
  calculateAgeOnlyYear,
  isValidIndianPhone,
  showLoadingImage,
  validateID,
} from "../Utility/Utility";
import MenuBar from "../common/MenuBar";
import GenericDropDown from "../common/GenericDropDown";
import GenericTextField from "../common/GenericTextField";
import GenericDatePicker from "../common/GenericDatePicker";

const maritalStatusOptions = ["Single", "Married", "Divorced", "Widowed"];


function NewPatient({ tempNewPatientId, onNewPatientAdded }) {
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const { auth } = useContext(authContext);
  const [patientDetails, setPatientDetails] = useState({ photo: "" });
  const [ageInYears, setAgeInYears] = useState();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [phoneValidationError, setPhoneValidationError] = useState(false);

  // Single state variable for loading state
  const [isSaving, setIsSaving] = useState(false);

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  function getStyles(option, selectedOptions, theme) {
    return {
      fontWeight: selectedOptions.includes(option)
        ? theme.typography.fontWeightMedium
        : theme.typography.fontWeightRegular,
    };
  }

  const idPatterns = {
    Aadhaar: /^[0-9]{0,12}$/, // allow typing up to 12 digits
    "PAN Card": /^[A-Z]{0,5}[0-9]{0,4}[A-Z]{0,1}$/i,
    Passport: /^[A-PR-WYa-pr-wy]?[0-9]{0,7}$/,
    "Driving License": /^[A-Z]{0,2}[0-9]{0,2}[0-9]{0,4}[0-9]{0,7}$/i,
    "Govt. Employee Id": /^[A-Za-z0-9]{0,20}$/,
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // const partialIndianPhone = /^(\+91[\s-]?|0)?[6-9]?\d{0,9}$/;
    // if (name === "phone") {
    //   if (!partialIndianPhone.test(value)) return;
    //   setPatientDetails((prev) => ({
    //     ...prev,
    //     [name]: value,
    //   }));
    //   return;
    // }

    // if (name === "emergencyPhone") {
    //   if (!partialIndianPhone.test(value)) return;
    //   setPatientDetails((prev) => ({
    //     ...prev,
    //     emergencyContactPhoneNumber: value, // note: update the correct state key
    //   }));
    //   return;
    // }

    // NATIONAL ID VALIDATION
    if (name === "nationalId") {
      const type = patientDetails.nationalIdType; // selected type
      if (type && idPatterns[type] && !idPatterns[type].test(value)) {
        return; // block invalid input
      }

      return setPatientDetails((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (name === "ageInYears") {
      const age = parseInt(value, 10);
      if (!isNaN(age)) {
        const today = new Date();
        const birthDate = new Date(
          today.getFullYear() - age,
          today.getMonth(),
          today.getDate()
        );
        const dobStr = birthDate.toISOString().split("T")[0];

        return setPatientDetails((prev) => ({
          ...prev,
          ageInYears: value,
          dateOfBirth: dobStr,
        }));
      }
    }

    setPatientDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDoBChange = (value) => {
    //const { name, value } = value;
    setPatientDetails((prev) => ({
      ...prev,
      ["dateOfBirth"]: value,
      ["ageInYears"]: calculateAgeOnlyYear(value),
    }));
  };

  // const handlePhotoUpload = (e) => {
  //   const file = e.target.files[0];
  //   if (file) {
  //     setPatientDetails((prev) => ({
  //       ...prev,
  //       photo: URL.createObjectURL(file),
  //     }));
  //   }
  // };
  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileSizeLimit = 1 * 1024 * 1024; // 1MB
    const allowedTypes = ["image/jpeg", "image/png", "image/gif"];

    if (!allowedTypes.includes(file.type)) {
      alert("Only JPG, PNG, and GIF files are allowed.");
      return;
    }

    if (file.size > fileSizeLimit) {
      alert("File size must be less than 1MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setPatientDetails((prev) => ({
        ...prev,
        photo: e.target.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handlePhotoDelete = () => {
    setPatientDetails((prev) => ({
      ...prev,
      photo: "",
    }));
  };

  const handleSave = async () => {
    // Define required fields and their corresponding error messages
    const requiredFields = [
      {
        field: "firstName",
        message: "Filling in all mandatory fields is required",
      },
      {
        field: "prefix",
        message: "Filling in all mandatory fields is requiredx is required",
      },
      {
        field: "dateOfBirth",
        message: "Filling in all mandatory fields is required",
      },
      {
        field: "phone",
        message: "Filling in all mandatory fields is required",
      },
      {
        field: "gender",
        message: "Filling in all mandatory fields is required",
      },
      {
        field: "lastName",
        message: "Filling in all mandatory fields is required",
      },
    ];

    setIsSubmitted(true); // Set form as submitted

    // Check for missing required fields
    for (const { field, message } of requiredFields) {
      if (!patientDetails[field]) {
        showSnackbar(message, "error");
        return; // Stop execution if any required field is missing
      }
    }

    if (patientDetails.phone === patientDetails.emergencyContactPhoneNumber) {
      showSnackbar(
        "Patient phone number and Emergency Phone Number can't be same",
        "error"
      );
      return;
    }

    try {
      // Set saving state to true to show loading overlay
      setIsSaving(true);

      // If all required fields are filled, proceed to save
      const result = await addPatient(auth, patientDetails);
      if (result.isSuccess) {
        showSnackbar("Patient saved successfully", "success");
        onNewPatientAdded(tempNewPatientId);
      } else {
        showSnackbar("Failed to save patient: " + result.data, "error");
      }
    } catch (error) {
      showSnackbar("An error occurred while saving patient", "error");
    } finally {
      // Always reset saving state when operation completes
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      {/* Loading Overlay - using only isSaving */}
      {isSaving && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(216, 214, 214, 0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1301, // One level above Dialog
          }}
        >
          {showLoadingImage()}
        </div>
      )}
      <Grid2 size={12} container direction="column" spacing={1}>
        <Grid2 container size={12}>
          <Grid2 size={12}>
            <MenuBar showSave={true} onSave={handleSave} />
          </Grid2>
        </Grid2>
        <Grid2
          size={12}
          container
          spacing={1}
          sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}
        >
          <Grid2
            size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}
            container
            spacing={2}
          >
            <Grid2>
              <Card
                className="outerCard"
                variant="outlined"
                sx={{ padding: "10px", width: "100%" }}
              >
                <CardHeader
                  subheader="Patient's Details"
                  sx={{ paddingX: 0 }}
                ></CardHeader>
                <Grid2 container spacing={2}>
                  {/* Prefix, First Name, Last Name */}
                  <Grid2 size={12} container alignItems="center" spacing={2}>
                    <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                      <GenericDropDown
                        label="Prefix"
                        value={patientDetails.prefix || ""}
                        onChange={handleChange}
                        options={Prefixes.map((option) => ({
                          value: option,
                          label: option,
                        }))}
                        required
                        error={isSubmitted && !patientDetails.prefix}
                        name="prefix"
                        size="small"
                        // InputLabelProps={{
                        //   sx: {
                        //     top: "-7px", // Adjust as needed
                        //   },
                        // }}
                        sx={{
                          width: "100%",
                          "& .MuiFormLabel-asterisk": {
                            color: "red",
                          },
                        }}
                        MenuProps={MenuProps}
                        getStyles={(option) =>
                          getStyles(option, [patientDetails.prefix], theme)
                        }
                      />
                    </Grid2>
                    <Grid2
                      size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                      mt={0}
                    >
                      <GenericTextField
                        variant="outlined"
                        label="Suffix"
                        name="suffix"
                        size="small"
                        fullWidth
                        value={patientDetails.suffix || ""}
                        onChange={handleChange}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                      <GenericTextField
                        variant="outlined"
                        label="First Name"
                        name="firstName"
                        size="small"
                        fullWidth
                        value={patientDetails.firstName || ""}
                        onChange={handleChange}
                        required // Show asterisk to indicate required field
                        error={isSubmitted && !patientDetails.firstName} // Show error only after submission
                        sx={{
                          "& .MuiFormLabel-asterisk": {
                            color: "red", // Make the asterisk red
                          },
                        }}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                      <GenericTextField
                        variant="outlined"
                        label="Last Name"
                        name="lastName"
                        size="small"
                        fullWidth
                        value={patientDetails.lastName || ""}
                        onChange={handleChange}
                        required
                        error={isSubmitted && !patientDetails.lastName}
                        sx={{
                          "& .MuiFormLabel-asterisk": {
                            color: "red",
                          },
                        }}
                      />
                    </Grid2>
                  </Grid2>

                  <Grid2 container size={12} spacing={2} direction="column">
                    {/* Row for Grid 1 and Grid 2 */}
                    <Grid2 container size={12} spacing={2}>
                      {/* Grid 1 - Age */}

                      {/* Grid 2 - Date of Birth */}
                      {/* <Grid2
                        size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                        backgroundColor="white"
                      >
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Date of Birth"
                            maxDate={dayjs()}
                            name="dateOfBirth"
                            fullWidth
                            required
                            value={dayjs(patientDetails.dateOfBirth)}
                            slotProps={{
                              textField: { size: "small", margin: "dense" },
                            }}
                            onChange={handleDoBChange}
                            error={isSubmitted && !patientDetails?.dateOfBirth} // Show error only after submission
                            sx={{
                              width: "100%",
                              "& .MuiFormLabel-asterisk": {
                                color: "red", // Make the asterisk red
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid2> */}
                      {/* Grid 2 - Date of Birth */}
                      <Grid2
                        size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                        backgroundColor="white"
                      >
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <GenericDatePicker
                            label="Date of Birth"
                            value={
                              patientDetails?.dateOfBirth
                                ? dayjs(patientDetails.dateOfBirth)
                                : null
                            }
                            onChange={(value) => {
                              setPatientDetails((prev) => ({
                                ...prev,
                                dateOfBirth: value
                                  ? value.format("YYYY-MM-DD")
                                  : "",
                                ageInYears: value
                                  ? calculateAgeOnlyYear(
                                      value.format("YYYY-MM-DD")
                                    )
                                  : "",
                              }));
                            }}
                            maxDate={dayjs()}
                            returnDayjsObject={true}
                            required={true}
                            error={isSubmitted && !patientDetails?.dateOfBirth}
                            displayFormat="DD-MMM-YYYY"
                          />
                        </LocalizationProvider>
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                        <GenericTextField
                          label="Age"
                          name="ageInYears"
                          fullWidth
                          size="small"
                          value={patientDetails.ageInYears || ""}
                          slotProps={{
                            inputLabel: { shrink: ageInYears },
                          }}
                          onChange={handleChange}
                          type="number"
                        />
                      </Grid2>
                    </Grid2>

                    {/* Row for Grid 3 and Grid 4 */}
                    <Grid2 container size={12}>
                      {/* Grid 4 - Phone */}
                      <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                        <GenericTextField
                          margin="dense"
                          size="small"
                          label="Phone"
                          name="phone"
                          fullWidth
                          inputType="number"
                          value={patientDetails.phone || ""}
                          onChange={handleChange}
                          required // Make the field required
                          error={isSubmitted && !patientDetails.phone} // Show error if the field is empty
                          //helperText={!patientDetails.phone ? "Phone is required" : ""} // Error message
                          sx={{
                            "& .MuiFormLabel-asterisk": {
                              color: "red", // Make the asterisk red
                            },
                          }}
                        />
                      </Grid2>
                      {/* Grid 3 - Email */}
                      <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                        <GenericTextField
                          margin="dense"
                          size="small"
                          label="Email"
                          name="email"
                          inputType="email"
                          fullWidth
                          value={patientDetails.email}
                          onChange={handleChange}
                        />
                      </Grid2>
                    </Grid2>
                  </Grid2>

                  {/* Other Details */}
                  <Grid2 size={12} container alignItems="center" spacing={2}>
                    <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }}>
                      <GenericDropDown
                        label="Gender"
                        value={patientDetails.gender || ""}
                        onChange={handleChange}
                        options={Genders.map((option) => ({
                          value: option,
                          label: option,
                        }))}
                        required={true}
                        error={isSubmitted && !patientDetails.gender}
                        name="gender"
                        size="small"
                        InputLabelProps={{
                          sx: {
                            top: "-7px", // Adjust as needed
                          },
                        }}
                        sx={{
                          "& .MuiFormLabel-asterisk": {
                            color: "red",
                          },
                        }}
                        MenuProps={MenuProps}
                        getStyles={(option) =>
                          getStyles(option, [patientDetails.gender], theme)
                        }
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }}>
                      <GenericDropDown
                        label="Marital Status"
                        value={patientDetails.maritalStatus || ""}
                        onChange={handleChange}
                        options={maritalStatusOptions.map((option) => ({
                          value: option,
                          label: option,
                        }))}
                        name="maritalStatus"
                        size="small"
                        InputLabelProps={{
                          sx: {
                            top: "-7px", // Adjust as needed
                          },
                        }}
                        sx={{}}
                        MenuProps={MenuProps}
                        getStyles={(option) =>
                          getStyles(
                            option,
                            [patientDetails.maritalStatus],
                            theme
                          )
                        }
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }}>
                      <GenericDropDown
                        label="Blood Group"
                        value={patientDetails.bloodGroup || ""}
                        onChange={handleChange}
                        options={BloodGroupOptions.map((option) => ({
                          value: option.value,
                          label: option.displayValue,
                        }))}
                        InputLabelProps={{
                          sx: {
                            top: "-7px", // Adjust as needed
                          },
                        }}
                        name="bloodGroup"
                        size="small"
                        sx={{}}
                        MenuProps={MenuProps}
                        getStyles={(option) =>
                          getStyles(option, [patientDetails.bloodGroup], theme)
                        }
                      />
                    </Grid2>
                  </Grid2>
                </Grid2>
              </Card>
            </Grid2>

            <Grid2>
              <Card
                className="outerCard"
                variant="outlined"
                sx={{ padding: "10px", width: "100%" }}
              >
                <CardHeader
                  subheader="Address Details"
                  sx={{ paddingX: 0 }}
                ></CardHeader>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <GenericTextField
                      label="House No"
                      name="addressHouseNo"
                      fullWidth
                      size="small"
                      value={patientDetails.addressHouseNo}
                      onChange={handleChange}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <GenericTextField
                      label="Street"
                      name="addressStreet"
                      size="small"
                      fullWidth
                      value={patientDetails.addressStreet}
                      onChange={handleChange}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <GenericTextField
                      label="City"
                      name="addressCity"
                      fullWidth
                      size="small"
                      value={patientDetails.addressCity}
                      onChange={handleChange}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <GenericTextField
                      label="State"
                      name="addressState"
                      fullWidth
                      size="small"
                      value={patientDetails.addressState}
                      onChange={handleChange}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <GenericTextField
                      label="Country"
                      name="addressCountry"
                      fullWidth
                      size="small"
                      value={patientDetails.addressCountry}
                      onChange={handleChange}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <GenericTextField
                      label="Pin Code"
                      name="addressPIN"
                      fullWidth
                      size="small"
                      value={patientDetails.addressPIN}
                      onChange={handleChange}
                    />
                  </Grid2>
                </Grid2>
              </Card>
            </Grid2>
            <Grid2 size={12}>
              <Card
                className="outerCard"
                variant="outlined"
                sx={{ padding: "10px", width: "100%" }}
              >
                <CardHeader
                  subheader="National ID"
                  sx={{ paddingX: 0 }}
                ></CardHeader>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <GenericDropDown
                      label="National ID Type"
                      value={patientDetails.nationalIdType || ""}
                      onChange={handleChange}
                      options={NationalIdTypes.map((option) => ({
                        value: option,
                        label: option,
                      }))}
                      name="nationalIdType"
                      size="small"
                      InputLabelProps={{
                        sx: {
                          top: "-7px", // Adjust as needed
                        },
                      }}
                      sx={{}}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <GenericTextField
                      label="ID Number"
                      name="nationalId"
                      fullWidth
                      size="small"
                      value={patientDetails.nationalId}
                      onChange={handleChange}
                    />
                  </Grid2>
                </Grid2>
              </Card>
            </Grid2>
            <Grid2 size={12}>
              <Card
                className="outerCard"
                variant="outlined"
                sx={{ padding: "10px", width: "100%" }}
              >
                <CardHeader
                  subheader="Emergency Contact Details"
                  sx={{ paddingX: 0 }}
                ></CardHeader>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <GenericTextField
                      variant="outlined"
                      label=" Name"
                      size="small"
                      name="emergencyContactPerson"
                      fullWidth
                      value={patientDetails.emergencyContactPerson}
                      onChange={handleChange}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <GenericTextField
                      label="Phone"
                      name="emergencyPhone"
                      fullWidth
                      size="small"
                      value={patientDetails.emergencyContactPhoneNumber}
                      onChange={handleChange}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <GenericTextField
                      label="Relationship"
                      name="emergencyRelationship"
                      fullWidth
                      size="small"
                      value={patientDetails.emergencyContactRelationship}
                      onChange={handleChange}
                    />
                  </Grid2>
                </Grid2>
              </Card>
            </Grid2>
          </Grid2>

          <Grid2
            size={{ xs: 8, sm: 8, md: 8, lg: 2, xl: 2 }}
            container
            alignItems="flex-start"
            justifyContent="flex-start"
            direction="column"
          >
            <Card
              className="outerCard"
              variant="outlined"
              sx={{
                padding: "10px",
                width: "100%",
              }}
            >
              {/* Card Header */}
              <CardHeader
                sx={{
                  textAlign: "center",
                }}
                subheader="Upload Image"
              />

              {/* Card Content */}
              <CardContent>
                <Grid2
                  container
                  alignItems="center"
                  justifyContent="center"
                  spacing={2}
                >
                  <Grid2 size={12} container justifyContent="center">
                    <img
                      src={
                        patientDetails.photo ||
                        "./placeholder-image-male-gender.webp"
                      }
                      alt="PatientPhoto"
                      width="120"
                      height="120"
                      className="border rounded p-0"
                    />
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ padding: 0 }}
                    >
                      Image size limit: 1MB
                    </Typography>
                  </Grid2>

                  {/* File Input (Hidden) */}
                  <input
                    type="file"
                    accept="image/jpeg, image/png, image/gif, image/webp, jpg, png, gif"
                    hidden
                    id="photo-upload"
                    onChange={handlePhotoUpload}
                  />

                  {/* Upload and Delete Buttons */}
                  <Grid2
                    container
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                  >
                    {/* Upload Button */}
                    <Grid2>
                      <label htmlFor="photo-upload">
                        <IconButton component="span">
                          <PhotoCamera />
                        </IconButton>
                      </label>
                    </Grid2>

                    {/* Delete Button (Only Shown When Photo is Uploaded) */}
                    {patientDetails.photo && (
                      <Grid2>
                        <IconButton onClick={handlePhotoDelete}>
                          <Delete />
                        </IconButton>
                      </Grid2>
                    )}
                  </Grid2>
                </Grid2>
              </CardContent>
            </Card>
          </Grid2>
        </Grid2>
      </Grid2>
    </Box>
  );
}

export default NewPatient;
